import { ArrowLeft, Loader2 } from "lucide-react";
import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { Field } from "../components/Fields";
import { LogoMark } from "../components/LogoMark";
import { useAuth } from "../lib/auth";

type LocationState = {
  from?: { pathname?: string };
};

export function AuthPage() {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  if (user) {
    return <Navigate to="/app" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "");
    const password = String(form.get("password") || "");
    const fullName = String(form.get("fullName") || "");

    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(email, password, fullName);
      }
      navigate(state?.from?.pathname || "/app", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="auth-page">
      <Link className="quiet-link" to="/">
        <ArrowLeft size={17} />
        Back
      </Link>
      <section className="auth-card">
        <div className="auth-heading">
          <LogoMark className="logo-mark-large" />
          <h1>{mode === "login" ? "Welcome back" : "Create your workspace"}</h1>
          <p>
            {mode === "login"
              ? "Sign in to keep every property, deadline, and document in one place."
              : "Set up your account and start your first property workspace."}
          </p>
        </div>

        <form className="stack-form" onSubmit={handleSubmit}>
          {mode === "register" ? (
            <Field label="Name">
              <input name="fullName" autoComplete="name" placeholder="Victoria Buyer" />
            </Field>
          ) : null}
          <Field label="Email">
            <input name="email" type="email" autoComplete="email" required placeholder="you@example.com" />
          </Field>
          <Field label="Password">
            <input
              name="password"
              type="password"
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              required
              minLength={8}
              placeholder="At least 8 characters"
            />
          </Field>
          {error ? <p className="form-error">{error}</p> : null}
          <button className="button button-primary full-width" type="submit" disabled={submitting}>
            {submitting ? <Loader2 className="spin" size={17} /> : null}
            {mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <button
          className="text-button"
          type="button"
          onClick={() => {
            setError(null);
            setMode((current) => (current === "login" ? "register" : "login"));
          }}
        >
          {mode === "login" ? "Need an account? Create one" : "Already have an account? Sign in"}
        </button>
      </section>
    </main>
  );
}
